import { logAnalyticEvent } from 'utils/analytics';
import { clevertapEvents } from 'types/baseTypes';
import { ServiceAreaStatus } from 'containers/settings/service-areas/constants';

const DISPATCH_MAP_LIBRARIES = ['maps', 'marker', 'drawing'] as const;
const DISPATCH_MAP_PRELOAD_IDLE_TIMEOUT_MS = 5000;

let dispatchMapLibrariesPreloadPromise: Promise<void> | null = null;
let dispatchMapChunksPreloaded = false;

const isGoogleMapsImportLibraryReady = () =>
  typeof window !== 'undefined' && typeof window.google?.maps?.importLibrary === 'function';

/**
 * Waits until the Maps JS API bootstrap (`importLibrary`) is available.
 * Relies on `loadGoogleMapsScript()` having been called elsewhere in the app shell.
 */
export const whenGoogleMapsCoreReady = (onReady: () => void, onTimeout?: () => void): (() => void) => {
  if (isGoogleMapsImportLibraryReady()) {
    onReady();
    return () => undefined;
  }

  let done = false;

  const complete = () => {
    if (done || !isGoogleMapsImportLibraryReady()) return;
    done = true;
    window.clearInterval(pollId);
    window.clearTimeout(timeoutId);
    onReady();
  };

  const pollId = window.setInterval(complete, 200);
  const script = document.getElementById('googleMapsScript') as HTMLScriptElement | null;
  const handleScriptLoad = () => complete();

  if (script) {
    script.addEventListener('load', handleScriptLoad);
  }

  const timeoutId = window.setTimeout(() => {
    if (done) return;
    done = true;
    window.clearInterval(pollId);
    script?.removeEventListener('load', handleScriptLoad);
    logAnalyticEvent(clevertapEvents.WebGoogleMapsScriptLoaded, {
      'Dispatch Map Libraries Preloaded': false,
      reason: 'timeout'
    });
    onTimeout?.();
  }, 60_000);

  return () => {
    done = true;
    script?.removeEventListener('load', handleScriptLoad);
    window.clearInterval(pollId);
    window.clearTimeout(timeoutId);
  };
};

export const preloadDispatchMapGoogleLibraries = (): Promise<void> => {
  if (dispatchMapLibrariesPreloadPromise) {
    return dispatchMapLibrariesPreloadPromise;
  }

  dispatchMapLibrariesPreloadPromise = new Promise<void>((resolve, reject) => {
    // Initialized before assignment so synchronous `onReady` can call it without TDZ errors.
    let cleanup: () => void = () => undefined;

    cleanup = whenGoogleMapsCoreReady(
      () => {
        cleanup();

        Promise.all(DISPATCH_MAP_LIBRARIES.map(library => google.maps.importLibrary(library)))
          .then(() => {
            logAnalyticEvent(clevertapEvents.WebGoogleMapsScriptLoaded, {
              'Dispatch Map Libraries Preloaded': true,
              libraries: DISPATCH_MAP_LIBRARIES.join(',')
            });
            resolve();
          })
          .catch(error => {
            dispatchMapLibrariesPreloadPromise = null;
            logAnalyticEvent(clevertapEvents.WebGoogleMapsScriptLoaded, {
              'Dispatch Map Libraries Preloaded': false,
              reason: 'import_failed'
            });
            reject(error);
          });
      },
      () => {
        cleanup();
        dispatchMapLibrariesPreloadPromise = null;
        reject(new Error('Google Maps core failed to load within timeout'));
      }
    );
  });

  return dispatchMapLibrariesPreloadPromise;
};

export const preloadDispatchMapChunks = () => {
  if (dispatchMapChunksPreloaded) return;

  dispatchMapChunksPreloaded = true;

  void import('pages/MapsPage');
  void import('containers/dispatch/DispatchMap');
  void import('containers/dispatch/components/DispatchMapView');
  void import('containers/dispatch/components/DispatchMapMarkers');
  void import('containers/dispatch/components/MapsMarkers');
  void import('containers/settings/service-areas/components/DrawingWrapper');
  void import('containers/settings/service-areas/hooks/useDrawingManager');
};

export const preloadDispatchMapServiceAreas = (
  teamId: number,
  getServiceAreas: (args: { teamId: string; params: Record<string, unknown> }) => unknown
) => {
  getServiceAreas({
    teamId: teamId.toString(),
    params: {
      status: ServiceAreaStatus.ENABLED,
      page: 0,
      limit: null
    }
  });
};

export const preloadDispatchMap = (
  teamId?: number,
  getServiceAreas?: (args: { teamId: string; params: Record<string, unknown> }) => unknown
) => {
  preloadDispatchMapChunks();
  void preloadDispatchMapGoogleLibraries();

  if (teamId && getServiceAreas) {
    preloadDispatchMapServiceAreas(teamId, getServiceAreas);
  }
};

export const scheduleDispatchMapPreload = (callback: () => void) => {
  if (typeof requestIdleCallback !== 'undefined') {
    const handle = requestIdleCallback(callback, { timeout: DISPATCH_MAP_PRELOAD_IDLE_TIMEOUT_MS });
    return () => cancelIdleCallback(handle);
  }

  const handle = window.setTimeout(callback, 2000);
  return () => clearTimeout(handle);
};

export const resetDispatchMapPreloadState = () => {
  dispatchMapLibrariesPreloadPromise = null;
  dispatchMapChunksPreloaded = false;
};
